import React, { useState, useEffect, ChangeEvent } from 'react';
import api from 'api';
import { connect, useSelector, useDispatch } from 'react-redux';
import {
  Grid, TableCell, TableRow, TableBody,
  Dialog, DialogActions, DialogContent,
  Button as MaterialButton, Checkbox, FormControlLabel, TextField,
} from '@material-ui/core';
import {
  selectSelectedSupplier,
} from 'store/suppliersList/Selector';
import { startLoading, finishLoading } from 'hooks/useProgress';
import { editSupplierData as editSupplierDataAction, toggleActivity } from 'store/suppliersList/ActionCreator';
import { OtherCertificatesList, Supplier, SupplierCardFile } from 'store/suppliersList/types';
import { AppState } from 'store/RootReducer';
import { ThunkDispatch } from 'store/StoreConfig';
import { showSnackbar } from 'hooks/useSnackbar';
import cn from './SupplierCardDetails.module.scss';
import { showSnackbarErrorMessage } from 'services/snackbarService';
import { DatePicker } from '@material-ui/pickers';
import { CalendarTodayOutlined, Add, Star, StarBorder, InsertDriveFileOutlined } from '@material-ui/icons';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Button } from 'components/common/Button/Button';
import { UploadButton } from 'components/SupplierCard/Tabs/FilesTab/UploadButton/UploadButton';
import { OtherCertificateRow } from 'components/SupplierCard/SupplierCardDetails/OtherDocumentsComponent/OtherDocumentsComponent';
import { selectIsAuthenticated } from 'store/microsoftGraph/Selector';
import { DriveItem, useFiles } from 'hooks/useFiles';
import { useCanEditSrm } from 'hooks/useHasRole';
import { ConfirmRenameModal } from '../Tabs/FilesTab/ConfirmRenameModal/ConfirmRenameModal';

type MapStateToProps = {
  selectedSupplier?: Supplier;
};

type MapDispatchToProps = {
  editSupplierData: (supplierId: string, supplier: Supplier) => void;
};

type SupplierCardDetailsProps = MapStateToProps & MapDispatchToProps;

var uploadingOtherCertificatesFiles: Array<File> = [];
var otherCertificatesHidden: OtherCertificatesList = [];

const SupplierCardDetailsComponent = React.memo(({
  selectedSupplier,
  editSupplierData,
}: SupplierCardDetailsProps) => {
  const [name, setName] = useState('');
  const [currencies, setCurrencies] = useState<Array<{ id: number; code: string }>>([]);
  const {
    getFiles, uploadFile, createDefaultFolders
  } = useFiles();
  const [insuranceFiles, setInsuranceFiles] = useState<Array<DriveItem>>([]);
  const [otherCertificatesFiles, setOtherCertificatesFiles] = useState<Array<DriveItem>>([]);
  const [otherCertificates, setOtherCertificates] = useState<OtherCertificatesList>(selectedSupplier?.supplierCardOtherFiles || []);
  const [insuranceDate, setInsuranceDate] = useState(selectedSupplier?.supplierCardInsurance?.validUntil as Date || null);
  const [insuranceFileName, setInsuranceFileName] = useState(selectedSupplier?.supplierCardInsurance?.filename as string || '');
  const [confirmOpenInsurance, setConfirmOpenInsurance] = useState(false);
  const [confirmDeleteRows, setConfirmDeleteRows] = useState(false);
  const [newInsuranceFileName, setNewInsuranceFileName] = useState('');
  const [uploadedInsuranceFiles, setUploadedInsuranceFiles] = useState<File[]>([]);
  const [uploadingInsuranceFile, setUploadingInsuranceFile] = useState<File>();
  const authenticated = useSelector(selectIsAuthenticated);
  const canEditSrm = useCanEditSrm();
  const insuranceEditable = authenticated && canEditSrm;
  const dispatch = useDispatch<ThunkDispatch>();
  const [blockedReason, setBlockedReason] = useState(selectedSupplier?.blockedReason || '');
  const [pendingBlock, setPendingBlock] = useState(false);
  const [pendingUnblock, setPendingUnblock] = useState(false);
  const [warningReason, setWarningReason] = useState(selectedSupplier?.warningReason || '');
  const [pendingWarning, setPendingWarning] = useState(false);
  const [pendingUnwarning, setPendingUnwarning] = useState(false);
  const [insuranceDateError, setInsuranceDateError] = useState(selectedSupplier?.supplierCardInsurance?.isDateError)
  const [insuranceFileError, setInsuranceFileError] = useState(selectedSupplier?.supplierCardInsurance?.isFileError)
  const [insuredAmount, setInsuredAmount] = useState<number | null>(selectedSupplier?.supplierCardInsurance?.insuredAmount ?? null);
  const [insuredCurrencyId, setInsuredCurrencyId] = useState<number | null>(selectedSupplier?.supplierCardInsurance?.insuredAmountCurrencyId ?? null);


  const handleNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setName(value);
  };

  useEffect(() => {
    api.getCurrencies().then(setCurrencies).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedSupplier?.blocked) {
      setPendingUnblock(false);
    }
  }, [selectedSupplier?.blocked]);

  useEffect(() => {
    if (!selectedSupplier?.warningDate) {
      setPendingUnwarning(false);
    }
  }, [selectedSupplier?.warningDate]);

  const fetchFiles = async (): Promise<void> => {
    var myFolders = await getFiles()
    if (myFolders.length !== 0) {
      var myInsuranceFiles = await getFiles(myFolders[0].id);  // Set myInsurance as first folder files ('1. Insurance')
      var myOtherCertificatesFiles = await getFiles(myFolders[5].id);  // Set myOther as 6th folder files ('6. Other')
      setInsuranceFiles(myInsuranceFiles);
      setOtherCertificatesFiles(myOtherCertificatesFiles);
    }
    else {
      setInsuranceFiles([]);
      setOtherCertificatesFiles([]);
    }
  }

  const handleDateChange = (date: MaterialUiPickersDate): void => {
    setInsuranceDate(date as Date);
  }

  const handleInsuranceFileNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setInsuranceFileName(value);
  }


  function handleProceed() {
    handleConfirmDeleteRowsClose();
    RemoveEmptyOtherFiles();
    handleSaveButton()
  }

  function handleCancel() {
    handleConfirmDeleteRowsClose();
  }


  const handleSaveButton = async (): Promise<void> => {
    if (!selectedSupplier) {
      return;
    }
    startLoading();
    var folders: Array<DriveItem> = [];
    var defaultFolders = {
      id: selectedSupplier.id,
      children: folders,
    };
    try {
      folders = await getFiles();
      defaultFolders.children = folders;
    } catch {
    }
    try {
      if (folders.length === 0) {
        try {
          defaultFolders = await createDefaultFolders(name);
          await fetchFiles();
        } catch (err) {
        }
      }
      if (otherCertificatesHidden && otherCertificatesHidden.filter(otherFile => otherFile.filename === '' && otherFile.validUntil === null).length > 0) {
        finishLoading();
        setConfirmDeleteRows(true)
        return;
      }
      var insurance: SupplierCardFile = {
        validUntil: insuranceDate,
        filename: insuranceFileName === "" ? null : insuranceFileName,
        uploadDate: new Date(),
        supplierCardId: selectedSupplier?.id,
        insuredAmount: insuredAmount,
        insuredAmountCurrencyId: insuredCurrencyId,
      }
      var addedSupplier = {
        ...selectedSupplier,
        supplierCardInsurance: insurance,
        supplierCardOtherFiles: otherCertificatesHidden,
        name,
        blocked: pendingBlock || Boolean(selectedSupplier?.blocked),
        blockedReason: (pendingBlock || Boolean(selectedSupplier?.blocked)) ? blockedReason.trim() : null,
        blockedDate: pendingBlock ? new Date().toISOString() : selectedSupplier?.blockedDate,
        warningDate: pendingWarning
          ? new Date().toISOString()
          : (pendingUnwarning ? null : selectedSupplier?.warningDate ?? null),
        warningReason: (pendingWarning || (Boolean(selectedSupplier?.warningDate) && !pendingUnwarning))
          ? warningReason.trim()
          : null,
        warnedByUserId: pendingUnwarning ? null : (selectedSupplier?.warnedByUserId ?? null),
      };
      var insuranceFolder = folders[0]?.id ?? defaultFolders.children?.[0]?.id;
      var otherFileFolder = folders[5]?.id ?? defaultFolders.children?.[5]?.id;
      addedSupplier = RemoveEmptyAllFiles(addedSupplier)

      const updatedCertificates = [...(addedSupplier.supplierCardOtherFiles ?? [])];
      addedSupplier = resetErrors(addedSupplier);
      var resFile = addedSupplier.supplierCardOtherFiles?.filter(x => x.filename === '');
      var resDate = addedSupplier.supplierCardOtherFiles?.filter(x => x.validUntil === null);
      if (updatedCertificates.length > 0) {
        [resFile, resDate].forEach(result => {
          if (result?.length > 0) {
            updatedCertificates.forEach(x => {
              if (result?.map(item => item.keyValue).includes(x.keyValue)) {
                if (result === resFile) {
                  x.isFileError = true;
                }
                if (result === resDate) {
                  x.isDateError = true;
                }
              }
            });
            setOtherFiles(updatedCertificates);
          }
        });
      }
      if (name.trim() === "" || name.trim().length === 0) {
        showSnackbar({
          message: "Supplier name can not be empty",
          type: 'error',
          autoHideDuration: 10000
        });
      } else if (addedSupplier.supplierCardInsurance?.validUntil === null) {
        setInsuranceDateError(true)
        showSnackbar({
          message: "Insurance date can not be empty",
          type: 'error',
          autoHideDuration: 10000
        });
      } else if (addedSupplier.blocked && !addedSupplier.blockedReason) {
        showSnackbar({
          message: "Blocked reason is required",
          type: 'error',
          autoHideDuration: 10000
        });
      } else if (addedSupplier.warningDate && !addedSupplier.warningReason) {
        showSnackbar({
          message: "Warning reason is required",
          type: 'error',
          autoHideDuration: 10000
        });
      } else if (addedSupplier.supplierCardInsurance?.filename === '' || addedSupplier.supplierCardInsurance?.filename === null) {
        setInsuranceFileError(true)
        showSnackbar({
          message: "Insurance file can not be empty",
          type: 'error',
          autoHideDuration: 10000
        });
      } else if (addedSupplier.supplierCardOtherFiles?.find(c => c.validUntil === null)) {
        showSnackbar({
          message: "Other certificates date can not be empty",
          type: 'error',
          autoHideDuration: 10000
        });
      } else if (addedSupplier.supplierCardOtherFiles?.find(c => c.filename === '')) {
        addedSupplier.supplierCardOtherFiles?.find(c => c.filename === '')
        showSnackbar({
          message: "Other certificates file can not be empty",
          type: 'error',
          autoHideDuration: 10000
        });
      } else {
        if (insuranceFolder && uploadingInsuranceFile !== undefined) {
          await handleInsuranceFileUpload(uploadingInsuranceFile, insuranceFolder);
          setUploadingInsuranceFile(undefined);
        }
        if (otherFileFolder && uploadingOtherCertificatesFiles.length > 0) {
          await handleOtherCertificatesUpload(uploadingOtherCertificatesFiles, otherFileFolder);
          uploadingOtherCertificatesFiles = [];
        }
        try { await fetchFiles(); } catch {}
        await editSupplierData(selectedSupplier.id, addedSupplier);
        setPendingBlock(false);
        setPendingWarning(false);
        setPendingUnwarning(false);
        setWarningReason(addedSupplier.warningReason || '');
        if (selectedSupplier !== undefined) {
          selectedSupplier.supplierCardOtherFiles = addedSupplier.supplierCardOtherFiles;
          selectedSupplier.supplierCardInsurance = addedSupplier.supplierCardInsurance;
        }
        if (selectedSupplier.supplierCardInsurance != null) {
          setInsuranceDateError(false);
          setInsuranceFileError(false);
        }
        showSnackbar({
          message: 'Supplier info was successfully updated.',
          type: 'success',
        });
      }
    } catch (err) {
      showSnackbarErrorMessage(err);
    }
    finishLoading();
  };

  const handleConfirmCloseInsurance = (): void => {
    setConfirmOpenInsurance(false);
    setNewInsuranceFileName('');
  };

  const handleConfirmDeleteRowsClose = (): void => {
    setConfirmDeleteRows(false);
  };

  function resetErrors(supplier: Supplier): Supplier {
    if (supplier.supplierCardInsurance != null) {
      setInsuranceDateError(false);
      setInsuranceFileError(false);
    }
    supplier.supplierCardOtherFiles?.map(x => {
      x.isDateError = false;
      x.isFileError = false;
    })
    return supplier;
  }

  function RemoveEmptyOtherFiles() {
    if (otherCertificates !== undefined) {
      var value = otherCertificates.filter(otherFile => otherFile.filename !== '' || otherFile.validUntil !== null)
      setOtherFiles(value);
    }
  }

  function RemoveEmptyAllFiles(addedSupplier: any): any {
    if (addedSupplier.supplierCardInsurance?.filename === null &&
      addedSupplier.supplierCardInsurance.validUntil === null)
      addedSupplier.supplierCardInsurance = null;
    if (addedSupplier.supplierCardOtherFiles?.length === 0)
      addedSupplier.supplierCardOtherFiles = null;
    return addedSupplier;
  }

  useEffect(() => {
    if (!selectedSupplier) {
      return;
    }
    const { name: supplierName, supplierCardInsurance: insurance, supplierCardOtherFiles: otherFiles } = selectedSupplier;
    setName(supplierName);
    setInsuranceDate(insurance?.validUntil as Date || null);
    setInsuranceFileName(insurance?.filename as string || "");
    setInsuredAmount(insurance?.insuredAmount ?? null);
    setInsuredCurrencyId(insurance?.insuredAmountCurrencyId ?? null);
    setBlockedReason(selectedSupplier?.blockedReason || '');
    if (otherFiles !== null) {
      for (let i = 1; i < otherFiles?.length; i++) {
        otherFiles[i - 1].keyValue = i;
      }
    }
    setOtherCertificates(otherFiles);
    otherCertificatesHidden = otherFiles
    fetchFiles();
    uploadingOtherCertificatesFiles = [];
    setInsuranceDateError(false);
    setInsuranceFileError(false);
  }, [selectedSupplier]);

  const handleInsuranceFileUploading = async (file: File): Promise<void> => {
    setUploadingInsuranceFile(file);
    setInsuranceFileName(file.name);
  };

  const handleInsuranceFileUpload = async (file: File, id: number): Promise<void> => {
    try {
      setUploadedInsuranceFiles([...uploadedInsuranceFiles, file]);
      await uploadFile(id, file);  // Upload to the 1st folder ('1. Insurance')
    } catch (err) {
    }
  }

  const handleOtherCertificatesUpload = async (file: File[], id: number): Promise<void> => {
    let otherFiles = otherCertificatesHidden;
    if (otherFiles === undefined || otherFiles.length === 0)
      otherFiles = [{}];

    otherFiles[0].uploadDate = new Date();
    try {
      uploadingOtherCertificatesFiles.forEach(async (fileToUpload) => {
        await uploadFile(id, fileToUpload);
      });
    } catch (err) {
    }

  }

  const handleInsuranceFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const { files: inputFiles } = event.target;
    await newFileNameHandle(inputFiles, true);
  };

  const handleOtherCertificatesChange = async (event: ChangeEvent<HTMLInputElement>, keyValue: number = 0, validUntil: Date): Promise<void> => {
    const { files: inputFiles } = event.target;
    await newFileNameHandle(inputFiles, false, keyValue, validUntil);
  };

  function handleOtherCertificatesDelete(event: ChangeEvent<HTMLInputElement>, keyValue: number) {
    var fileToRemove = [...otherCertificates].filter((_item, index) => index === keyValue)
    setOtherFiles([...otherCertificates].filter((_item, index) => index !== keyValue));
    uploadingOtherCertificatesFiles = uploadingOtherCertificatesFiles.filter(item => item.name !== fileToRemove[0]?.filename);
  };

  const setOtherCertificatesItem = (fileName: any, fileDate: any, index: any) => {
    var otherFiles = [];
    if (otherCertificates && otherCertificates.length > 0)
      otherFiles = [...otherCertificates];
    else
      otherFiles.push({});
    otherFiles[index].filename = fileName;
    otherFiles[index].validUntil = fileDate;
    otherFiles[index].uploadDate = new Date();
    otherFiles[index].supplierCardId = selectedSupplier?.id;
    setOtherFiles(otherFiles)
  }

  function handleAddRow() {
    const newItem: SupplierCardFile = {
      id: 0,
      filename: "",
      validUntil: null,
      uploadDate: null,
      supplierCardId: selectedSupplier?.id,
      keyValue: otherCertificates?.length + 1
    };
    setOtherFiles([...(otherCertificates || []), newItem])
  }

  function setOtherFiles(otherFiles: any) {
    if (otherFiles !== null) {
      for (let i = 1; i < otherFiles?.length; i++) {
        otherFiles[i - 1].keyValue = i;
      }
    }
    otherCertificatesHidden = otherFiles;
    setOtherCertificates([...otherFiles]);
  }

  async function newFileNameHandle(inputFiles: FileList | null, isInsurance: boolean, keyValue: number = 0, validUntil: any = null) {
    if (!inputFiles || inputFiles.length === 0) {
      return;
    }
    const file = Array.from(inputFiles || [])[0];
    let i = 1;
    const nameParts = file.name.split('.');
    const format = nameParts.pop();
    let newName = `${nameParts.join('.')}_(${i}).${format}`;
    var isNewName = false;
    if (isInsurance) {
      if (insuranceFiles.find((f) => f.name === file.name)) {
        isNewName = true;
        while (insuranceFiles.find((f) => f.name === newName)) {
          newName = `${nameParts.join('.')}_(${++i}).${format}`;
        }
      }
    }
    else {
      if (otherCertificatesFiles.find((f) => f.name === file.name)) {
        isNewName = true;
        while (otherCertificatesFiles.find((f) => f.name === newName)) {
          newName = `${nameParts.join('.')}_(${++i}).${format}`;
        }
      }
    }
    if (!isInsurance && uploadingOtherCertificatesFiles.find((f) => f.name === file.name || f.name === newName)) {
      isNewName = true;
      while (uploadingOtherCertificatesFiles.find((f) => f.name === newName)) {
        newName = `${nameParts.join('.')}_(${++i}).${format}`;

      }
    }
    if (isNewName === true) {
      if (isInsurance) {
        setInsuranceFileName(newName);
        setNewInsuranceFileName(newName);
        setUploadingInsuranceFile(file);
        setConfirmOpenInsurance(true);
        return;
      } else {
        await setOtherCertificatesItem(newName, otherCertificatesHidden[keyValue]?.validUntil ?? validUntil, keyValue)
        const newFile = new File([file], newName);
        uploadingOtherCertificatesFiles.push(newFile);

        return;
      }
    }

    if (isInsurance) {
      setInsuranceFileName(file.name);
      setUploadingInsuranceFile(file);
    } else {
      await setOtherCertificatesItem(file.name, otherCertificatesHidden[keyValue]?.validUntil ?? validUntil, keyValue)

      uploadingOtherCertificatesFiles.push(file)
    }
  }

  return (
    <div className={cn.container}>
      <div className={cn.topRow}>
        <div>
          <div className={cn.supplierName}>Name:</div>
          <div className={cn.supplierInfo}>
            <input
              type="search"
              className={cn.headerInput}
              value={name}
              onChange={handleNameChange}
            />
          </div>
        </div>
        <div className={cn.scoreBlock}>
          <span className={cn.scoreLabel}>
            Score: {Array.from({ length: 5 }, (_, i) =>
              selectedSupplier && i < selectedSupplier.score
                ? <Star key={i} fontSize="small" />
                : <StarBorder key={i} fontSize="small" />,
            )}
          </span>
          <FormControlLabel
            classes={{ root: cn.metaCheckbox, label: cn.metaLabel }}
            label="List in Irena Desktop"
            control={(
              <Checkbox
                checked={Boolean(selectedSupplier?.isActive)}
                color="default"
                onChange={(e: ChangeEvent<HTMLInputElement>) => dispatch(toggleActivity(e.target.checked))}
                size="small"
                disabled={!canEditSrm}
              />
            )}
          />
          <div className={cn.blockedSection}>
            <FormControlLabel
              classes={{ root: cn.metaCheckbox, label: cn.metaLabel }}
              label="Blocked"
              control={(
                <Checkbox
                  checked={Boolean(selectedSupplier?.blocked) || pendingBlock}
                  color="default"
                  disabled={!canEditSrm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (!selectedSupplier) return;
                    if (e.target.checked) {
                      setPendingBlock(true);
                      setPendingUnblock(false);
                      setBlockedReason('');
                    } else {
                      setPendingBlock(false);
                      setPendingUnblock(true);
                      setBlockedReason('');
                      dispatch(editSupplierDataAction(selectedSupplier.id, {
                        ...selectedSupplier,
                        blocked: false,
                        blockedReason: null,
                        blockedDate: null,
                      }));
                    }
                  }}
                  size="small"
                />
              )}
            />
            {(pendingBlock || (Boolean(selectedSupplier?.blocked) && !pendingUnblock)) && (
              <TextField
                label="Reason"
                value={blockedReason}
                onChange={(e) => setBlockedReason(e.target.value)}
                variant="outlined"
                size="small"
                disabled={!canEditSrm}
                fullWidth
                multiline
                minRows={2}
                error={!blockedReason.trim() && (pendingBlock || Boolean(selectedSupplier?.blocked))}
                helperText={!blockedReason.trim() ? 'Reason is required' : ''}
              />
            )}
          </div>
          <div className={cn.blockedSection}>
            <FormControlLabel
              classes={{ root: cn.metaCheckbox, label: cn.metaLabel }}
              label="Warning"
              control={(
                <Checkbox
                  checked={Boolean(selectedSupplier?.warningDate) || pendingWarning}
                  color="default"
                  disabled={!canEditSrm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (!selectedSupplier) return;
                    if (e.target.checked) {
                      setPendingWarning(true);
                      setPendingUnwarning(false);
                      setWarningReason('');
                    } else {
                      setPendingWarning(false);
                      setPendingUnwarning(true);
                      setWarningReason('');
                      dispatch(editSupplierDataAction(selectedSupplier.id, {
                        ...selectedSupplier,
                        warningDate: null,
                        warningReason: null,
                        warnedByUserId: null,
                      }));
                    }
                  }}
                  size="small"
                />
              )}
            />
            {(pendingWarning || (Boolean(selectedSupplier?.warningDate) && !pendingUnwarning)) && (
              <TextField
                label="Reason"
                value={warningReason}
                onChange={(e) => setWarningReason(e.target.value)}
                variant="outlined"
                size="small"
                disabled={!canEditSrm}
                fullWidth
                multiline
                minRows={2}
                error={!warningReason.trim() && (pendingWarning || Boolean(selectedSupplier?.warningDate))}
                helperText={!warningReason.trim() ? 'Reason is required' : ''}
              />
            )}
          </div>
        </div>
      </div>
      <div className={cn.innerColumns}>
        <div className={cn.innerLeft}>
              <table className={cn.certificatesTable}>
                <TableBody>
                  <TableRow classes={{ root: cn.tableBodyRow }}>
                    <TableCell classes={{ head: cn.tableBodyCell }} padding="none">
                      <div className={cn.label}>CMR insurance:</div>
                      <div className={cn.sublabel}>Expiration Date:</div>
                      <div className={cn.tableDateCell}>
                        <DatePicker
                          value={insuranceDate}
                          format="yyyy-MM-dd"
                          onChange={handleDateChange}
                          minDate={new Date()}
                          animateYearScrolling={false}
                          className={insuranceDateError !== true ? cn.tableBodyDatePicker : cn.tableBodyDatePicker + " " + cn.error}
                          InputProps={{
                            disableUnderline: true,
                            endAdornment: (
                              <div className={cn.calendarAdornment}>
                                <CalendarTodayOutlined className={cn.calendarIcon} />
                              </div>
                            ),
                          }}
                          disabled={!insuranceEditable}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      classes={{ head: cn.tableBodySecondCell }}
                      padding="none"
                    >
                      {insuranceFileName && (
                        <span className={cn.fileIcon} title={insuranceFileName}>
                          <InsertDriveFileOutlined fontSize="small" />
                        </span>
                      )}
                    </TableCell>
                    <TableCell
                      classes={{ head: cn.tableBodySecondCell }}
                      padding="none"
                    >
                      <UploadButton
                        onChange={handleInsuranceFileChange}
                        id="insurance"
                        className={cn.icon}
                        disabled={!insuranceEditable}
                      />
                    </TableCell>
                    {confirmOpenInsurance && (
                      <ConfirmRenameModal
                        open={confirmOpenInsurance}
                        file={uploadingInsuranceFile}
                        newFileName={newInsuranceFileName}
                        onSubmit={handleInsuranceFileUploading}
                        onClose={handleConfirmCloseInsurance}
                      />
                    )}
                  </TableRow>
                  <TableRow classes={{ root: cn.tableBodyRow }}>
                    <TableCell classes={{ head: cn.tableBodyCell }} padding="none" colSpan={3}>
                      <div className={cn.sublabel}>Insured Amount:</div>
                      <div className={cn.insuredAmountRow}>
                        <TextField
                          type="number"
                          className={cn.tableBodyDatePicker}
                          value={insuredAmount ?? ''}
                          onChange={(e) => setInsuredAmount(e.target.value ? Number(e.target.value) : null)}
                          disabled={!insuranceEditable}
                          InputProps={{ disableUnderline: true }}
                        />
                        <select
                          className={cn.tableBodyInput}
                          value={insuredCurrencyId ?? ''}
                          onChange={(e) => setInsuredCurrencyId(e.target.value ? Number(e.target.value) : null)}
                          disabled={!insuranceEditable}
                          style={{ height: 34 }}
                        >
                          <option value="">—</option>
                          {currencies.map(c => (
                            <option key={c.id} value={c.id}>{c.code}</option>
                          ))}
                        </select>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </table>
            </div>
            <div className={cn.innerRight}>
              <div className={cn.label}>Other certificates:
                <div className={cn.buttonRow}>
                  <Button
                    onClick={handleAddRow}
                    color="primary"
                    className={cn.button}
                    disabled={!authenticated}
                  >
                    <Add className={cn.newSupplierPlusIcon} />
                  </Button>
                </div>
              </div>
              <table className={cn.certificatesTable}>
                <TableBody>
                  {otherCertificates?.map((item, index) => (
                    <OtherCertificateRow
                      key={index}
                      keyValue={index}
                      otherCertificatesItem={item}
                      setOtherCertificatesItemChild={setOtherCertificatesItem}
                      handleOtherCertificatesChange={handleOtherCertificatesChange}
                      handleOtherCertificatesDelete={handleOtherCertificatesDelete}
                    />
                  ))}
                  {confirmDeleteRows && (
                    <Dialog
                      open={confirmDeleteRows}
                      onClose={handleConfirmDeleteRowsClose}
                    >
                      <DialogContent>
                        <div className={cn.content} data-role="content">
                          <span>There are empty Other certificates. Before saving they will be discarded and removed. Continue?</span>
                        </div>
                      </DialogContent>
                      <DialogActions>
                        <MaterialButton onClick={handleProceed} data-role="rename">Proceed</MaterialButton>
                        <MaterialButton onClick={handleCancel} data-role="replace">Cancel</MaterialButton>
                      </DialogActions>
                    </Dialog>
                  )}
                </TableBody>
              </table>
            </div>
          </div>
          <div className={cn.headerSaveButtonRoot}>
            <button
              type="button"
              className={cn.headerSaveButton}
              onClick={handleSaveButton}
            >
              Save
            </button>
          </div>
    </div>
  );
});

const mapStateToProps = (state: AppState): MapStateToProps => ({
  selectedSupplier: selectSelectedSupplier(state),
});

const mapDispatchToProps: MapDispatchToProps = {
  editSupplierData: editSupplierDataAction,
};

export const SupplierCardDetails = connect(mapStateToProps, mapDispatchToProps)(SupplierCardDetailsComponent);
